import { useState } from 'react'
import { Plus, X, Database } from 'lucide-react'
import { SPARK_TYPES, resolveType } from '../../../data/etlSchemaPropagation'
import './editors.css'

const TYPE_OPTIONS = [
  { value: SPARK_TYPES.STRING,    label: 'StringType' },
  { value: SPARK_TYPES.INTEGER,   label: 'IntegerType' },
  { value: SPARK_TYPES.LONG,      label: 'LongType' },
  { value: SPARK_TYPES.FLOAT,     label: 'FloatType' },
  { value: SPARK_TYPES.DOUBLE,    label: 'DoubleType' },
  { value: SPARK_TYPES.DECIMAL,   label: 'DecimalType' },
  { value: SPARK_TYPES.BOOLEAN,   label: 'BooleanType' },
  { value: SPARK_TYPES.DATE,      label: 'DateType' },
  { value: SPARK_TYPES.TIMESTAMP, label: 'TimestampType' },
  { value: SPARK_TYPES.BINARY,    label: 'BinaryType' },
  { value: SPARK_TYPES.ARRAY,     label: 'ArrayType' },
  { value: SPARK_TYPES.MAP,       label: 'MapType' },
  { value: SPARK_TYPES.VECTOR,    label: 'VectorType' },
]

/**
 * Schema editor for viewing and editing a node's output schema.
 * Shows columns as a table: Name | Type | Nullable.
 *
 * Read-only when `readOnly` is true (propagated schemas).
 * Editable when `readOnly` is false (source nodes, manual schemas).
 *
 * Props:
 *   schema   - { columns: [{ name, type, nullable }], source }
 *   onChange - (newSchema) => void
 *   readOnly - boolean
 *   disabled - boolean (from executing state)
 */
function SchemaEditor({ schema, onChange, readOnly = false, disabled = false }) {
  const [newColName, setNewColName] = useState('')
  const columns = schema?.columns || []
  const source = schema?.source || 'propagated'

  const updateColumn = (idx, key, val) => {
    const updated = columns.map((c, i) => {
      if (i !== idx) return c
      return { ...c, [key]: val }
    })
    onChange({ ...schema, columns: updated, source: 'manual' })
  }

  const addColumn = () => {
    if (!newColName.trim()) return
    const updated = [
      ...columns,
      { name: newColName.trim(), type: SPARK_TYPES.STRING, nullable: true },
    ]
    onChange({ ...schema, columns: updated, source: 'manual' })
    setNewColName('')
  }

  const removeColumn = (idx) => {
    onChange({
      ...schema,
      columns: columns.filter((_, i) => i !== idx),
      source: 'manual',
    })
  }

  const sourceLabel = {
    inferred: 'Inferred from source',
    manual: 'Manually defined',
    propagated: 'Propagated from upstream',
  }

  return (
    <div className="schema-editor">
      <div className="schema-editor-header">
        <Database size={13} />
        <span className="schema-editor-count">{columns.length} column{columns.length !== 1 ? 's' : ''}</span>
        <span className="schema-editor-source">{sourceLabel[source] || source}</span>
      </div>

      {columns.length > 0 && (
        <div className="schema-editor-table">
          <div className="schema-editor-thead">
            <span className="schema-col-name">Column</span>
            <span className="schema-col-type">Type</span>
            <span className="schema-col-null">Null</span>
            {!readOnly && <span className="schema-col-action"></span>}
          </div>
          {columns.map((col, idx) => (
            <div key={idx} className="schema-editor-row">
              {readOnly ? (
                <span className="schema-col-name schema-col-readonly">{col.name}</span>
              ) : (
                <input
                  className="schema-col-name-input"
                  value={col.name}
                  onChange={(e) => updateColumn(idx, 'name', e.target.value)}
                  disabled={disabled}
                  placeholder="name"
                />
              )}
              {readOnly ? (
                <span className="schema-col-type schema-col-readonly">{col.type}</span>
              ) : (
                <select
                  className="schema-col-type-select"
                  value={col.type}
                  onChange={(e) => updateColumn(idx, 'type', e.target.value)}
                  disabled={disabled}
                >
                  {TYPE_OPTIONS.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              )}
              {readOnly ? (
                <span className="schema-col-null schema-col-readonly">
                  {col.nullable !== false ? 'Yes' : 'No'}
                </span>
              ) : (
                <input
                  type="checkbox"
                  className="schema-col-null-check"
                  checked={col.nullable !== false}
                  onChange={(e) => updateColumn(idx, 'nullable', e.target.checked)}
                  disabled={disabled}
                  title="Nullable"
                />
              )}
              {!readOnly && (
                <button
                  className="editor-remove-btn"
                  onClick={() => removeColumn(idx)}
                  disabled={disabled}
                  title="Remove column"
                >
                  <X size={12} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {columns.length === 0 && (
        <div className="schema-editor-empty">
          No schema defined. {!readOnly ? 'Add columns manually or use Infer Schema.' : 'Connect a source with a known schema.'}
        </div>
      )}

      {!readOnly && (
        <div className="schema-editor-add">
          <input
            className="editor-input"
            value={newColName}
            onChange={(e) => setNewColName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); addColumn() }
            }}
            placeholder="Column name"
            disabled={disabled}
          />
          <button
            className="editor-add-btn"
            onClick={addColumn}
            disabled={disabled || !newColName.trim()}
            style={{ width: 'auto', marginTop: 0, padding: '5px 10px' }}
          >
            <Plus size={12} /> Add
          </button>
        </div>
      )}
    </div>
  )
}

export default SchemaEditor
